import { useId, useState } from 'react';
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORY_LABEL,
  type NotificationCategory,
} from '@/domain';
import {
  useMarkAllNotificationsRead,
  useNotificationSummary,
  useNotifications,
} from '@/application/usecases/useNotifications';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { SettingsIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { Modal } from '@/ui/primitives/Modal';
import { NotificationList } from './NotificationList';
import { useNotificationCenter } from './notificationCenterStore';
import { useOpenNotification } from './useOpenNotification';
import styles from './NotificationCenter.module.css';

const text = strings.notifications;

/** What the list is narrowed to: everything, what is not read yet, or one category. */
type Tag = 'all' | 'unread' | NotificationCategory;

/**
 * The notification centre. Section 7.6, rebuilt by the request of
 * 7 October 2026, section 2.
 *
 * Tags over the list narrow it: All, Unread, and one for each category, each
 * with the count of what is not read in it, counted by the server over
 * everything and not over what is on screen. The list reads newest first or
 * oldest first, gathered by day. A row opens its case in place and is marked
 * read as it does; Mark all as read clears the lot, and the bell's figure with
 * it.
 *
 * Settings sits over the list, beside the order, and not in the footer: three
 * buttons side by side did not fit a phone, and the first of them, this one,
 * was cut off at the edge of the dialog. The footer is the pair it is in every
 * other dialog. The request of 8 October 2026 (second).
 *
 * The narrowing and the order are asked of the server, so a person with three
 * hundred notifications finds the six unread ones without them being among the
 * first hundred.
 */
export function NotificationCenter({ onClose }: { onClose: () => void }) {
  const orderId = useId();
  const [tag, setTag] = useState<Tag>('all');
  const [oldestFirst, setOldestFirst] = useState(false);

  const summary = useNotificationSummary();
  const { data, isPending, isError } = useNotifications({
    ...(tag === 'all' || tag === 'unread' ? {} : { category: tag }),
    ...(tag === 'unread' ? { unread: true } : {}),
    ...(oldestFirst ? { oldestFirst: true } : {}),
  });
  const markAllRead = useMarkAllNotificationsRead();
  const showSettings = useNotificationCenter((state) => state.showSettings);
  const open = useOpenNotification(onClose);

  const rows = data ?? [];
  const unread = summary.data?.unread ?? 0;
  const countOf = (each: Tag): number | null =>
    each === 'all'
      ? null
      : each === 'unread'
        ? unread
        : (summary.data?.byCategory[each] ?? 0);

  const tags: { key: Tag; label: string }[] = [
    { key: 'all', label: text.all },
    { key: 'unread', label: text.unreadTag },
    ...NOTIFICATION_CATEGORIES.map((key) => ({ key, label: NOTIFICATION_CATEGORY_LABEL[key] })),
  ];

  return (
    <Modal
      title={text.title}
      lead={
        unread === 0 ? text.allRead : `${String(unread)} ${unread === 1 ? text.unreadOne : text.unread}`
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{text.close}</Button>
          <Button
            variant="primary"
            disabled={unread === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            {text.markAll}
          </Button>
        </>
      }
    >
      <div className={styles.tags} role="group" aria-label={text.tagsLabel}>
        {tags.map(({ key, label }) => {
          const count = countOf(key);
          return (
            <button
              key={key}
              type="button"
              className={`${styles.tag} ${tag === key ? styles.on : ''}`}
              aria-pressed={tag === key}
              onClick={() => setTag(key)}
            >
              {label}
              {count !== null && count > 0 && <span className={styles.count}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className={styles.bar}>
        <button
          type="button"
          className={styles.settings}
          aria-label={text.settings}
          title={text.settings}
          onClick={() => {
            onClose();
            showSettings();
          }}
        >
          <SettingsIcon />
          {text.settingsShort}
        </button>
        <div className={styles.order}>
          <label htmlFor={orderId}>{text.order}</label>
          <select
            id={orderId}
            value={oldestFirst ? 'oldest' : 'newest'}
            onChange={(event) => setOldestFirst(event.target.value === 'oldest')}
          >
            <option value="newest">{text.newestFirst}</option>
            <option value="oldest">{text.oldestFirst}</option>
          </select>
        </div>
      </div>

      {isPending && <SkeletonRows rows={3} label={strings.common.loadingNotifications} />}
      {isError && <p className={styles.note}>{strings.errors.server}</p>}

      {!isPending && !isError && rows.length === 0 && (
        <div className={styles.emptyBox}>
          <h3>{tag === 'all' ? text.empty : text.emptyFiltered}</h3>
          <p>{tag === 'all' ? text.emptyDetail : text.emptyFilteredDetail}</p>
        </div>
      )}

      {rows.length > 0 && <NotificationList rows={rows} onOpen={open} byDay label={text.title} />}
    </Modal>
  );
}
